import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { OpsService } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';

export interface MatrixRole {
  id: string | number;
  roleName: string;
  displayName?: string;
  description?: string;
  status?: string;
  immutable?: boolean;
  assignedUserCount?: number;
  permissionCodes?: string[];
  permissionCount?: number;
  permissionTotal?: number;
  holderUserId?: string | number | null;
  holderName?: string | null;
  holderDepartment?: string | null;
}

interface MatrixResource {
  id: string | number;
  resourceName: string;
  description?: string;
  label?: string;
  sortOrder?: number;
}

interface MatrixPermission {
  id: string | number;
  resourceRef: string | number;
  action: string;
  permissionCode: string;
}

interface MatrixRow {
  resource: MatrixResource;
  cells: Array<{
    action: string;
    permissionCode: string | null;
  }>;
}

/** Render plan: N/A trailing actions fold into colspan so those columns vanish for the row. */
interface MatrixActionView {
  action: string;
  permissionCode: string;
  colspan: number;
  /** Empty leading cell so later checkboxes stay under the correct header. */
  spacer?: boolean;
}

/**
 * Collapse trailing N/A Access/Read/Write slots into colspan on the last real checkbox
 * before each gap. Header stays 3 action columns; per-row only applicable actions emit <td>s.
 *
 * Deterministic: identical applicable patterns always yield identical colspans / column starts.
 * Example Access+Read (no Write): Access colspan=1, Read colspan=2 (covers Read+Write).
 */
export function buildActionViews(
  cells: Array<{ action: string; permissionCode: string | null }>,
): MatrixActionView[] {
  const applicable = cells
    .map((cell, index) => ({ ...cell, index }))
    .filter((cell): cell is { action: string; permissionCode: string; index: number } =>
      !!cell.permissionCode,
    );
  if (!applicable.length) return [];

  // Leading N/A columns: emit empty spacer cells so the first checkbox stays under its header.
  const views: MatrixActionView[] = [];
  for (let gap = 0; gap < applicable[0].index; gap += 1) {
    views.push({
      action: cells[gap].action,
      permissionCode: '',
      colspan: 1,
      spacer: true,
    });
  }

  for (let i = 0; i < applicable.length; i += 1) {
    const cell = applicable[i];
    const nextIndex = i + 1 < applicable.length ? applicable[i + 1].index : cells.length;
    views.push({
      action: cell.action,
      permissionCode: cell.permissionCode,
      colspan: nextIndex - cell.index,
    });
  }
  return views;
}

const ACTION_COLS = ['access', 'read', 'write'] as const;

function sameCodes(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b);
  return a.every((c) => set.has(c));
}

function toggleCodes(
  current: readonly string[],
  resourceName: string,
  action: string,
  checked: boolean,
  available: Set<string>,
): string[] {
  const next = new Set(current);
  const access = `${resourceName}_ACCESS`;
  const read = `${resourceName}_READ`;
  const write = `${resourceName}_WRITE`;

  if (checked) {
    if (action === 'write') {
      [write, read, access].forEach((c) => available.has(c) && next.add(c));
    } else if (action === 'read') {
      [read, access].forEach((c) => available.has(c) && next.add(c));
    } else if (action === 'access' && available.has(access)) {
      next.add(access);
    }
  } else if (action === 'access') {
    [access, read, write].forEach((c) => next.delete(c));
  } else if (action === 'read') {
    [read, write].forEach((c) => next.delete(c));
  } else if (action === 'write') {
    next.delete(write);
  }

  return [...next].sort();
}

@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './roles.component.html',
  styleUrl: './roles.component.scss',
})
export class RolesComponent implements OnInit {
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);

  readonly roles = signal<MatrixRole[]>([]);
  readonly resources = signal<MatrixResource[]>([]);
  readonly permissions = signal<MatrixPermission[]>([]);
  readonly selectedRoleId = signal<string | number | null>(null);
  readonly draftCodes = signal<string[]>([]);
  readonly savedCodes = signal<string[]>([]);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly message = signal<string | null>(null);
  readonly createOpen = signal(false);
  readonly editOpen = signal(false);
  readonly confirmSaveOpen = signal(false);
  newRoleName = '';
  newDescription = '';
  editDescription = '';
  editStatus: 'active' | 'inactive' = 'active';

  readonly selectedRole = computed(() => {
    const id = this.selectedRoleId();
    if (id == null) return null;
    return this.roles().find((r) => String(r.id) === String(id)) || null;
  });

  readonly immutable = computed(() => !!this.selectedRole()?.immutable);

  readonly dirty = computed(
    () => !this.immutable() && !sameCodes(this.draftCodes(), this.savedCodes()),
  );

  readonly availableCodes = computed(() => new Set(this.permissions().map((p) => p.permissionCode)));

  readonly matrixRows = computed<MatrixRow[]>(() => {
    const perms = this.permissions();
    const byResource = new Map<string, MatrixPermission[]>();
    for (const p of perms) {
      const key = String(p.resourceRef);
      if (!byResource.has(key)) byResource.set(key, []);
      byResource.get(key)!.push(p);
    }
    return this.resources()
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((resource) => {
        const list = byResource.get(String(resource.id)) || [];
        const byAction = new Map(list.map((p) => [p.action.toLowerCase(), p.permissionCode]));
        return {
          resource,
          cells: ACTION_COLS.map((action) => ({
            action,
            permissionCode: byAction.get(action) || null,
          })),
        };
      });
  });

  readonly grantedCount = computed(() => this.draftCodes().length);
  readonly availableCount = computed(() => this.permissions().length);

  actionViewsFor(row: MatrixRow): MatrixActionView[] {
    return buildActionViews(row.cells);
  }

  canManage(): boolean {
    return this.rbac.canModule('roles');
  }

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  async reload(): Promise<void> {
    if (!this.canManage()) {
      this.message.set('Only Admin can manage roles and permissions.');
      return;
    }
    this.loading.set(true);
    this.message.set(null);
    try {
      const matrix = await this.ops.listRolesMatrix();
      this.roles.set(matrix.items);
      this.resources.set(matrix.referenceData.resources || []);
      this.permissions.set(matrix.referenceData.permissions || []);
      const keepId = this.selectedRoleId();
      const next =
        (keepId != null && matrix.items.find((r) => String(r.id) === String(keepId))) ||
        matrix.items.find((r) => r.roleName === 'admin') ||
        matrix.items[0] ||
        null;
      if (next) this.selectRole(next.id, true);
      else this.selectedRoleId.set(null);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Failed to load roles matrix');
    } finally {
      this.loading.set(false);
    }
  }

  selectRole(roleId: string | number, force = false): void {
    if (!force && this.dirty()) {
      const ok = window.confirm('Discard unsaved permission changes?');
      if (!ok) return;
    }
    const role = this.roles().find((r) => String(r.id) === String(roleId));
    if (!role) return;
    this.selectedRoleId.set(role.id);
    const codes = [...(role.permissionCodes || [])].sort();
    this.savedCodes.set(codes);
    this.draftCodes.set(codes);
  }

  isChecked(permissionCode: string | null): boolean {
    if (!permissionCode) return false;
    return this.draftCodes().includes(permissionCode);
  }

  onToggle(row: MatrixRow, action: string, permissionCode: string | null, checked: boolean): void {
    if (this.immutable() || !permissionCode || this.busy()) return;
    const resourceName = row.resource.resourceName;
    this.draftCodes.set(
      toggleCodes(this.draftCodes(), resourceName, action, checked, this.availableCodes()),
    );
  }

  cancelDraft(): void {
    this.draftCodes.set([...this.savedCodes()]);
  }

  requestSave(): void {
    if (!this.dirty() || this.immutable()) return;
    this.confirmSaveOpen.set(true);
  }

  closeConfirmSave(): void {
    if (this.busy()) return;
    this.confirmSaveOpen.set(false);
  }

  async confirmSave(): Promise<void> {
    const role = this.selectedRole();
    if (!role || this.immutable()) return;
    this.busy.set(true);
    try {
      const result = await this.ops.updateRolePermissions(role.id, this.draftCodes());
      const codes = [...(result.permissionCodes || [])].sort();
      this.roles.update((list) =>
        list.map((r) =>
          String(r.id) === String(role.id)
            ? {
                ...r,
                permissionCodes: codes,
                permissionCount: result.permissionCount ?? codes.length,
              }
            : r,
        ),
      );
      this.savedCodes.set(codes);
      this.draftCodes.set(codes);
      this.confirmSaveOpen.set(false);
      this.message.set(`Permissions updated for ${role.displayName || role.roleName}.`);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Failed to save permissions');
    } finally {
      this.busy.set(false);
    }
  }

  openCreate(): void {
    if (!this.canManage()) return;
    this.newRoleName = '';
    this.newDescription = '';
    this.createOpen.set(true);
  }

  closeCreate(force = false): void {
    if (this.busy() && !force) return;
    this.createOpen.set(false);
  }

  async create(): Promise<void> {
    if (!this.newRoleName.trim()) {
      this.message.set('Role name is required.');
      return;
    }
    this.busy.set(true);
    try {
      this.message.set(
        await this.ops.createRole({
          roleName: this.newRoleName,
          description: this.newDescription,
        }),
      );
      this.closeCreate(true);
      await this.reload();
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Create role failed');
    } finally {
      this.busy.set(false);
    }
  }

  openEdit(): void {
    const role = this.selectedRole();
    if (!role || role.immutable) return;
    this.editDescription = role.description || '';
    this.editStatus = role.status === 'inactive' ? 'inactive' : 'active';
    this.editOpen.set(true);
  }

  closeEdit(force = false): void {
    if (this.busy() && !force) return;
    this.editOpen.set(false);
  }

  async saveEdit(): Promise<void> {
    const role = this.selectedRole();
    if (!role || role.immutable) return;
    this.busy.set(true);
    try {
      const updated = await this.ops.updateRoleMetadata(role.id, {
        description: this.editDescription,
        status: this.editStatus,
      });
      this.roles.update((list) =>
        list.map((r) =>
          String(r.id) === String(role.id)
            ? {
                ...r,
                description: updated.description ?? this.editDescription,
                status: updated.status ?? this.editStatus,
              }
            : r,
        ),
      );
      this.closeEdit(true);
      this.message.set(`Role “${role.displayName || role.roleName}” updated.`);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      this.busy.set(false);
    }
  }

  displayName(role: MatrixRole): string {
    return role.displayName || role.roleName;
  }

  sameId(a: string | number | null | undefined, b: string | number | null | undefined): boolean {
    if (a == null || b == null) return false;
    return String(a) === String(b);
  }
}
